import { useState } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Package, Truck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function BookingCalendar({ bookings, onDayClick }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Group bookings by date, then by client (booking_id)
  const bookingsByDate = {};
  
  bookings.forEach(booking => {
    const pickupDate = booking.pickup_date;
    const deliveryDate = booking.delivery_date;
    const bookingId = booking.id;
    
    // Check if pickup and delivery are on the same day (combined)
    const isSameDay = pickupDate && deliveryDate && pickupDate === deliveryDate;
    
    if (isSameDay) {
      // Combined: ramassage + livraison même jour
      const dateKey = pickupDate;
      if (!bookingsByDate[dateKey]) {
        bookingsByDate[dateKey] = { combined: 0, pickupsOnly: 0, deliveriesOnly: 0, bookings: {} };
      }
      if (!bookingsByDate[dateKey].bookings[bookingId]) {
        bookingsByDate[dateKey].combined++;
        bookingsByDate[dateKey].bookings[bookingId] = { 
          ...booking, 
          type: 'combined',
          hasPickup: true,
          hasDelivery: true
        };
      }
    } else {
      // Separate days
      if (pickupDate) {
        if (!bookingsByDate[pickupDate]) {
          bookingsByDate[pickupDate] = { combined: 0, pickupsOnly: 0, deliveriesOnly: 0, bookings: {} };
        }
        if (!bookingsByDate[pickupDate].bookings[bookingId]) {
          bookingsByDate[pickupDate].pickupsOnly++;
          bookingsByDate[pickupDate].bookings[bookingId] = { 
            ...booking, 
            type: 'pickup',
            hasPickup: true,
            hasDelivery: false
          };
        }
      }
      if (deliveryDate) {
        if (!bookingsByDate[deliveryDate]) {
          bookingsByDate[deliveryDate] = { combined: 0, pickupsOnly: 0, deliveriesOnly: 0, bookings: {} };
        }
        if (!bookingsByDate[deliveryDate].bookings[bookingId]) {
          bookingsByDate[deliveryDate].deliveriesOnly++;
          bookingsByDate[deliveryDate].bookings[bookingId] = { 
            ...booking, 
            type: 'delivery',
            hasPickup: false,
            hasDelivery: true
          };
        } else {
          // Already has pickup on this day, mark as combined
          bookingsByDate[deliveryDate].bookings[bookingId].hasDelivery = true;
          bookingsByDate[deliveryDate].bookings[bookingId].type = 'combined';
          bookingsByDate[deliveryDate].pickupsOnly--;
          bookingsByDate[deliveryDate].combined++;
        }
      }
    }
  });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const startDayOffset = (monthStart.getDay() + 6) % 7;

  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToToday = () => setCurrentMonth(new Date());

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            📅 Calendrier des réservations
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Aujourd'hui
            </Button>
            <span className="font-semibold text-lg min-w-[150px] text-center">
              {format(currentMonth, "MMMM yyyy", { locale: fr })}
            </span>
            <Button variant="ghost" size="icon" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-4 text-sm text-slate-500 mt-2">
          <span className="flex items-center gap-1">
            <RefreshCw className="w-3 h-3 text-purple-500" /> Ramassage + Livraison
          </span>
          <span className="flex items-center gap-1">
            <Package className="w-3 h-3 text-pink-500" /> Ramassage seul
          </span>
          <span className="flex items-center gap-1">
            <Truck className="w-3 h-3 text-green-500" /> Livraison seule
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map(day => (
            <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for offset */}
          {Array.from({ length: startDayOffset }).map((_, i) => (
            <div key={`empty-${i}`} className="h-20 bg-slate-50 rounded-lg" />
          ))}

          {/* Days */}
          {days.map(day => {
            const dateKey = format(day, "yyyy-MM-dd");
            const dayData = bookingsByDate[dateKey];
            const bookingsList = dayData ? Object.values(dayData.bookings) : [];
            const hasBookings = dayData && (dayData.combined > 0 || dayData.pickupsOnly > 0 || dayData.deliveriesOnly > 0);
            const isCurrentDay = isToday(day);

            return (
              <div
                key={dateKey}
                onClick={() => onDayClick && onDayClick(dateKey, bookingsList)}
                className={`h-20 p-1 rounded-lg border transition-all cursor-pointer hover:border-pink-300 hover:shadow-sm ${
                  isCurrentDay 
                    ? "bg-pink-50 border-pink-300" 
                    : hasBookings 
                      ? "bg-white border-slate-200" 
                      : "bg-slate-50 border-transparent"
                }`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isCurrentDay ? "text-pink-600" : "text-slate-700"
                }`}>
                  {format(day, "d")}
                </div>
                
                {hasBookings && (
                  <div className="space-y-0.5">
                    {dayData.combined > 0 && (
                      <div className="flex items-center gap-1 text-xs bg-purple-100 text-purple-700 rounded px-1 py-0.5">
                        <RefreshCw className="w-3 h-3" />
                        <span>{dayData.combined}</span>
                      </div>
                    )}
                    {dayData.pickupsOnly > 0 && (
                      <div className="flex items-center gap-1 text-xs bg-pink-100 text-pink-700 rounded px-1 py-0.5">
                        <Package className="w-3 h-3" />
                        <span>{dayData.pickupsOnly}</span>
                      </div>
                    )}
                    {dayData.deliveriesOnly > 0 && (
                      <div className="flex items-center gap-1 text-xs bg-green-100 text-green-700 rounded px-1 py-0.5">
                        <Truck className="w-3 h-3" />
                        <span>{dayData.deliveriesOnly}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
